<template>
  <div
    :class="[
      $style.config,
      $style.wrap,
      hideTextOverflow && $style.hideTextOverflow,
    ]"
  >
    <VueSelect
      v-model="selected"
      :is-clearable="clearable"
      :options="computedOptions"
      :placeholder="placeholder"
      :is-searchable="searchable"
      @option-selected="handleChange"
      @option-deselected="handleClear"
    />
  </div>
</template>

<script setup lang="ts">
import VueSelect from 'vue3-select-component'
import type { Option } from 'vue3-select-component'

const emit = defineEmits(['on-change', 'on-clear'])

const props = defineProps<{
  activeFilterValue?: string
  clearable?: boolean
  options: (Option<string> | string)[]
  placeholder?: string
  searchable?: boolean
  hideTextOverflow?: boolean // Forces ellipsis for multi-line text from active value
}>()

const selected = defineModel<string>({ default: '' })

const handleChange = (val: { label: string; value: string }) => {
  selected.value = val?.value || ''
  emit('on-change')
}

const handleClear = () => {
  emit('on-clear')
}

const computedOptions = computed(() => {
  let options = [] as Option<string>[]

  // Convert strings to option object
  if (props.options?.some?.((i) => typeof i === 'string')) {
    options = props.options.map((option) => {
      if (typeof option === 'string')
        return {
          label: option,
          value: option,
        }
      return option
    })
  } else if (props.options?.every?.((i) => typeof i === 'object')) {
    options = props.options
  }

  return options
})

onMounted(() => {
  selected.value = props.activeFilterValue || ''
})
</script>

<style lang="scss" module>
:global {
  :local(.wrap) {
    .vue-select {
      --vs-input-bg: var(--gray-1);
      --vs-input-outline: var(--gray-5);
      --vs-input-placeholder-color: var(--gray-7);

      --vs-padding: 0 7px 0 10px;
      --vs-border: 1.5px solid var(--gray-5);
      --vs-border-radius: var(--border-radius);

      --vs-font-size: 1.125rem;
      --vs-font-weight: 700;
      --vs-font-family: var(--roboto-condensed);
      --vs-text-color: var(--black);
      // --vs-line-height: 1.5;

      --vs-menu-offset-top: -3px;
      // --vs-menu-height: 200px;
      // --vs-menu-padding: 0;
      --vs-menu-border: 1.5px solid var(--gray-5);
      --vs-menu-bg: var(--gray-1);
      // --vs-menu-box-shadow: none;
      // --vs-menu-z-index: 2;

      // --vs-option-padding: 8px 12px;
      --vs-option-font-size: 1rem;
      --vs-option-font-weight: 700;
      // --vs-option-text-color: var(--vs-text-color);
      --vs-option-bg: var(--vs-menu-bg);
      --vs-option-hover-color: var(--vs-menu-bg);
      --vs-option-focused-color: var(--vs-menu-bg);
      --vs-option-selected-color: var(--vs-menu-bg);
      // --vs-option-disabled-color: #f4f4f5;
      // --vs-option-disabled-text-color: #52525b;

      // --vs-multi-value-gap: 4px;
      // --vs-multi-value-padding: 4px;
      // --vs-multi-value-font-size: 14px;
      // --vs-multi-value-font-weight: 400;
      // --vs-multi-value-line-height: 1;
      // --vs-multi-value-text-color: #3f3f46;
      // --vs-multi-value-bg: #f4f4f5;
      // --vs-multi-value-xmark-size: 16px;
      // --vs-multi-value-xmark-color: var(--vs-multi-value-text-color);

      // --vs-indicators-gap: 4px;
      // --vs-icon-size: 20px;
      // --vs-icon-color: var(--purple); Doesn't work
      // --vs-dropdown-transition: transform 0.25s ease-out;

      .control {
        height: 50px;
        box-shadow: none;
      }

      .menu {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }

      .menu-option {
        font-family: var(--base-font-family);

        &:hover,
        &.selected {
          color: var(--purple-light);
        }
      }

      .dropdown-icon {
        color: var(--purple);

        svg {
          fill: currentColor;
        }
      }
    }
  }

  :local(.hideTextOverflow) {
    .vue-select {
      .value-container {
        overflow: hidden;
        white-space: nowrap;
        flex-wrap: nowrap;
      }

      .single-value {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
}
</style>
